import React, { useState, useEffect, useRef } from "react";

const patterns = [
  {
    sequence: ["🐱", "🐶", "🐶", "🐱", "🐶", "🐶"],
    answer: "🐱",
    options: ["🐶", "🐱", "🐭"],
  },
  {
    sequence: ["🦁", "🦁", "🐯", "🦁", "🦁", "🐯"],
    answer: "🦁",
    options: ["🐯", "🦁", "🐻"],
  },
  {
    sequence: ["🐸", "🐢", "🐸", "🐢"],
    answer: "🐸",
    options: ["🐢", "🐸", "🦎"],
  },
  {
    sequence: ["🔔", "💬", "🚨", "🎵", "🔔", "💬", "🚨", "🎵"],
    answer: "🔔",
    options: ["🔔", "🚨", "🎵"],
  },
  {
    sequence: ["🌧️", "🧥", "☀️", "🧢", "🌧️", "🧥"],
    answer: "☀️",
    options: ["☀️", "🌧️", "🧣"],
  },
  {
    sequence: ["🐕", "🐈", "🐯", "🐻", "🐕", "🐈"],
    answer: "🐯",
    options: ["🐯", "🐕", "🐹"],
  },
  {
    sequence: ["🍎", "🧁", "🍕", "🥦", "🍎", "🧁"],
    answer: "🍕",
    options: ["🍕", "🥦", "🍩"],
  },
  {
    sequence: ["🕺", "🤖", "🕺", "🤖"],
    answer: "🕺",
    options: ["🤖", "🕺", "👾"],
  },
];

const styles = {
  body: {
    background:
      "linear-gradient(135deg, #ffe0b2, #b2dfdb, #d1c4e9)",
    backgroundSize: "400% 400%",
    animation: "gradientFlow 10s ease infinite",
    fontFamily: "'Comic Sans MS', cursive",
    textAlign: "center",
    padding: 30,
    color: "#333",
    minHeight: "100vh",
  },
  h1: {
    backgroundColor: "rgba(255,255,255,0.8)",
    display: "inline-block",
    padding: "10px 20px",
    borderRadius: 10,
    color: "#2e7d32",
    userSelect: "none",
  },
  patternContainer: {
    fontSize: 50,
    margin: "20px 0",
    minHeight: 80,
    backgroundColor: "rgba(255,255,255,0.9)",
    color: "#000",
    display: "inline-block",
    padding: 10,
    borderRadius: 12,
    userSelect: "none",
  },
  options: {
    margin: "20px auto",
    display: "flex",
    justifyContent: "center",
    gap: 20,
    flexWrap: "wrap",
  },
  option: {
    fontSize: 50,
    cursor: "pointer",
    transition: "transform 0.2s",
    backgroundColor: "rgba(255,255,255,0.9)",
    borderRadius: 10,
    padding: 10,
    userSelect: "none",
  },
  optionHover: {
    transform: "scale(1.2)",
  },
  result: {
    marginTop: 20,
    fontSize: 20,
    minHeight: 30,
    backgroundColor: "rgba(255,255,255,0.9)",
    color: "#1b5e20",
    display: "none",
    padding: "10px 20px",
    borderRadius: 10,
    whiteSpace: "pre-wrap",
  },
  resultVisible: {
    display: "inline-block",
  },
  animalLine: {
    display: "inline-block",
    animation: "march 2s linear infinite",
  },
  nextBtn: {
    marginTop: 20,
    padding: "10px 20px",
    fontSize: 16,
    backgroundColor: "#388e3c",
    color: "white",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    userSelect: "none",
  },
  nextBtnHover: {
    backgroundColor: "#2e7d32",
  },
  // keyframes can't be used inline, so added in CSS via useEffect below
};

const keyframesStyle = `
@keyframes gradientFlow {
  0% {background-position: 0% 50%;}
  50% {background-position: 100% 50%;}
  100% {background-position: 0% 50%;}
}
@keyframes march {
  0% { transform: translateX(0); }
  100% { transform: translateX(20px); }
}
`;

function shuffleArray(arr) {
  return arr.slice().sort(() => Math.random() - 0.5);
}

export default function ParadePattern() {
  const [currentPattern, setCurrentPattern] = useState(null);
  const [resultVisible, setResultVisible] = useState(false);
  const [resultMessage, setResultMessage] = useState("");
  const [optionHoverIndex, setOptionHoverIndex] = useState(null);
  const correctSoundRef = useRef(null);
  const wrongSoundRef = useRef(null);

  useEffect(() => {
    // Append keyframes style to document head once
    const styleTag = document.createElement("style");
    styleTag.textContent = keyframesStyle;
    document.head.appendChild(styleTag);
    loadPattern();

    return () => {
      document.head.removeChild(styleTag);
    };
  }, []);

  function loadPattern() {
    setResultVisible(false);
    setResultMessage("");
    const pattern = patterns[Math.floor(Math.random() * patterns.length)];
    setCurrentPattern(pattern);
  }

  function checkAnswer(choice) {
    if (!currentPattern) return;
    if (choice === currentPattern.answer) {
      correctSoundRef.current?.play();
      setResultMessage(
        `✅ Correct! Parade continues! ` +
          `\u00A0` +
          <span
            style={styles.animalLine}
          >
            {[...currentPattern.sequence, choice].join("")}
          </span>
      );
      // Because React elements can't be concatenated as strings, we'll render a separate div below for the parade
      setResultVisible(true);
    } else {
      wrongSoundRef.current?.play();
      setResultMessage("❌ Oops! Try again!");
      setResultVisible(true);
    }
  }

  return (
    <div style={styles.body}>
      <h3 style={styles.h1}>Parade Pattern</h3>
      <p>What comes next in the sequence?</p>

      <div style={styles.patternContainer} aria-live="polite" aria-atomic="true">
        {currentPattern ? currentPattern.sequence.join("") + "❓" : ""}
      </div>

      <div style={styles.options}>
        {currentPattern?.options.map((option, idx) => (
          <div
            key={idx}
            style={{
              ...styles.option,
              ...(optionHoverIndex === idx ? styles.optionHover : {}),
            }}
            onClick={() => checkAnswer(option)}
            onMouseEnter={() => setOptionHoverIndex(idx)}
            onMouseLeave={() => setOptionHoverIndex(null)}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                checkAnswer(option);
              }
            }}
            aria-label={`Select ${option}`}
          >
            {option}
          </div>
        ))}
      </div>

      <div
        style={{
          ...styles.result,
          ...(resultVisible ? styles.resultVisible : {}),
        }}
        aria-live="assertive"
      >
        {resultMessage && resultMessage.startsWith("✅") ? (
          <>
            ✅ Correct! Parade continues!{" "}
            <span style={styles.animalLine}>
              {[...currentPattern.sequence, currentPattern.answer].join("")}
            </span>
          </>
        ) : (
          resultMessage
        )}
      </div>

      <button style={styles.nextBtn} onClick={loadPattern}>
        Next Pattern
      </button>

      <audio
        id="jungleSound"
        src="https://cdn.pixabay.com/download/audio/2022/03/01/audio_6f33d0de7f.mp3?filename=jungle-ambience-6037.mp3"
        autoPlay
        loop
        muted={false}
        preload="auto"
      />
      <audio
        id="correctSound"
        src="https://cdn.pixabay.com/download/audio/2022/03/15/audio_1605d4d3e1.mp3?filename=correct-2-46134.mp3"
        ref={correctSoundRef}
        preload="auto"
      />
      <audio
        id="wrongSound"
        src="https://cdn.pixabay.com/download/audio/2022/03/15/audio_d1e1992c89.mp3?filename=wrong-47985.mp3"
        ref={wrongSoundRef}
        preload="auto"
      />
    </div>
  );
}
